import React from "react";
import cn from "classnames";
import { Braces, Check } from "lucide-react";
import { Card, CardTitle, Input } from "campaign-builder/ui";
import { FieldError, RadioDot, RequiredLabel } from "../targeting/fields";
import { Dropdown, type DropdownOption } from "./Dropdown";
import { isValidUrl, type TrackingConfigState, type TrackingMode } from "./useTrackingConfig";

interface TrackingConfigCardProps {
  state: TrackingConfigState;
  placeholders: string[];
  showErrors: boolean;
  update: (patch: Partial<TrackingConfigState>) => void;
  onPersistCoupon: () => void;
  onPersistQr: () => void;
}

const CheckboxCard = ({
  checked,
  title,
  description,
  onToggle,
  dataCy,
}: {
  checked: boolean;
  title: string;
  description: string;
  onToggle: () => void;
  dataCy: string;
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onToggle}
    data-cy={dataCy}
    className={cn(
      "flex flex-1 items-start gap-3 rounded-lg border border-solid p-4 text-left",
      checked ? "border-[#222336] dark:border-slate-300" : "border-border"
    )}
  >
    <span
      className={cn(
        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid",
        checked ? "border-[#ef2b55] bg-[#ef2b55]" : "border-slate-300 dark:border-accent"
      )}
    >
      {checked && <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
    </span>
    <span className="flex flex-col gap-0.5">
      <span className="text-foreground text-sm font-medium leading-5">{title}</span>
      <span className="text-muted-foreground text-xs leading-4">{description}</span>
    </span>
  </button>
);

const ModeCards = ({
  value,
  uniqueLabel,
  sameLabel,
  onChange,
  dataCyPrefix,
}: {
  value: TrackingMode | null;
  uniqueLabel: string;
  sameLabel: string;
  onChange: (mode: TrackingMode) => void;
  dataCyPrefix: string;
}) => (
  <div role="radiogroup" className="flex gap-2">
    {(["unique", "same"] as const).map((mode) => (
      <button
        key={mode}
        type="button"
        role="radio"
        aria-checked={value === mode}
        onClick={() => onChange(mode)}
        data-cy={`${dataCyPrefix}-${mode}`}
        className={cn(
          "flex flex-1 items-center gap-2 rounded-lg border border-solid px-4 py-3 text-left text-sm font-medium",
          value === mode
            ? "text-foreground border-[#222336] dark:border-slate-300"
            : "text-muted-foreground border-border"
        )}
      >
        <RadioDot selected={value === mode} />
        {mode === "unique" ? uniqueLabel : sameLabel}
      </button>
    ))}
  </div>
);

export const TrackingConfigCard: React.FC<TrackingConfigCardProps> = ({
  state,
  placeholders,
  showErrors,
  update,
  onPersistCoupon,
  onPersistQr,
}) => {
  const placeholderOptions: DropdownOption[] = placeholders.map((name) => ({
    value: name,
    label: name,
    icon: <Braces className="text-slate-400 h-4 w-4 shrink-0" />,
  }));

  const promoCodeError =
    showErrors && state.promoEnabled && state.promoMode === "same" && !state.staticCouponCode.trim()
      ? "Promo code is required when promo code is enabled."
      : null;
  const promoPlaceholderError =
    showErrors && state.promoEnabled && state.promoMode === "unique" && !state.promoPlaceholder
      ? "Select the placeholder for your promo code."
      : null;
  const qrUrlError =
    showErrors && state.qrEnabled && state.qrMode === "same"
      ? !state.qrCodeLink.trim()
        ? "QR code URL is required when QR code is enabled."
        : !isValidUrl(state.qrCodeLink)
        ? "Please enter a valid URL."
        : null
      : null;
  const qrPlaceholderError =
    showErrors && state.qrEnabled && state.qrMode === "unique" && !state.qrPlaceholder
      ? "Select the placeholder for your QR code."
      : null;

  return (
    <Card className="flex flex-col gap-5 p-[25px]" data-targeting-section="tracking">
      <div className="flex flex-col gap-1">
        <CardTitle>Tracking</CardTitle>
        <p className="text-muted-foreground text-sm leading-5">
          Configure your tracking metrics to effectively assess your campaign&apos;s performance post-launch.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <RequiredLabel>How will you measure this campaign?</RequiredLabel>
        <p className="text-muted-foreground text-xs leading-4">
          You can use promo codes, QR codes, or a combination of both.
        </p>
        <div className="flex gap-2">
          <CheckboxCard
            checked={state.promoEnabled}
            title="Promo codes"
            description="Enable this feature if your flyer will feature a trackable code/coupon."
            onToggle={() =>
              update({
                promoEnabled: !state.promoEnabled,
                promoMode: state.promoEnabled ? null : state.promoMode,
              })
            }
            dataCy="admTrackingPromoEnabledCheckbox"
          />
          <CheckboxCard
            checked={state.qrEnabled}
            title="QR Codes"
            description="Enable this feature if your flyer will feature a QR Code link."
            onToggle={() => update({ qrEnabled: !state.qrEnabled, qrMode: state.qrEnabled ? null : state.qrMode })}
            dataCy="admTrackingQrEnabledCheckbox"
          />
        </div>
      </div>

      {state.promoEnabled && (
        <div className="flex flex-col gap-2">
          <RequiredLabel>Promo code</RequiredLabel>
          <ModeCards
            value={state.promoMode}
            uniqueLabel="Unique code per recipient"
            sameLabel="Same code for everyone"
            onChange={(mode) => update({ promoMode: mode })}
            dataCyPrefix="admTrackingPromoMode"
          />
          {state.promoMode === "same" && (
            <div className="flex flex-col gap-2">
              <RequiredLabel id="admTrackingPromoCodeLabel">
                Enter the coupon code included in your flyer.
              </RequiredLabel>
              <Input
                value={state.staticCouponCode}
                placeholder="E.g.: PROMO50"
                onChange={(event) => update({ staticCouponCode: event.target.value })}
                onBlur={onPersistCoupon}
                data-cy="admTrackingPromoCodeInput"
                aria-invalid={!!promoCodeError}
                aria-describedby={promoCodeError ? "admTrackingPromoCodeError" : undefined}
              />
              <FieldError message={promoCodeError} id="admTrackingPromoCodeError" />
            </div>
          )}
          {state.promoMode === "unique" && (
            <div className="flex flex-col gap-2">
              <RequiredLabel>Which placeholder are you using for Promo codes?</RequiredLabel>
              <Dropdown
                value={state.promoPlaceholder}
                options={placeholderOptions}
                placeholder="Select a placeholder"
                invalid={!!promoPlaceholderError}
                onSelect={(value) => update({ promoPlaceholder: value })}
                dataCy="admPlaceholderPromoSelect"
              />
              <FieldError message={promoPlaceholderError} />
            </div>
          )}
        </div>
      )}

      {state.qrEnabled && (
        <div className="flex flex-col gap-2">
          <RequiredLabel>QR Code</RequiredLabel>
          <ModeCards
            value={state.qrMode}
            uniqueLabel="Unique URL per recipient"
            sameLabel="Same URL for everyone"
            onChange={(mode) => update({ qrMode: mode })}
            dataCyPrefix="admTrackingQrMode"
          />
          {state.qrMode === "same" && (
            <div className="flex flex-col gap-2">
              <RequiredLabel>What is the QR code URL?</RequiredLabel>
              <Input
                value={state.qrCodeLink}
                placeholder="https://"
                className={cn(qrUrlError && "border-destructive")}
                onChange={(event) => update({ qrCodeLink: event.target.value })}
                onBlur={onPersistQr}
                data-cy="admTrackingQrUrlInput"
                aria-invalid={!!qrUrlError}
                aria-describedby={qrUrlError ? "admTrackingQrUrlError" : undefined}
              />
              <FieldError message={qrUrlError} id="admTrackingQrUrlError" />
            </div>
          )}
          {state.qrMode === "unique" && (
            <div className="flex flex-col gap-2">
              <RequiredLabel>Which placeholder are you using for QR Codes?</RequiredLabel>
              <Dropdown
                value={state.qrPlaceholder}
                options={placeholderOptions}
                placeholder="Select a placeholder"
                invalid={!!qrPlaceholderError}
                onSelect={(value) => update({ qrPlaceholder: value })}
                dataCy="admPlaceholderQrSelect"
              />
              <FieldError message={qrPlaceholderError} />
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
